import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildZoneProductGridRows,
  filterLocationsByGridSearch,
  filterLocationsForZoneIndex,
  getLocationsWithZones,
  getZoneGridColumns,
  getZoneOpenIssueId,
  isZoneTotalCountAlert,
  parseZoneTotal,
  productHasSoldQty,
  shouldCenterZoneProducts,
  zoneHasSoldProducts,
} from './zoneIndexGridUtils';

const ZONE_IMAGE_PLACEHOLDER =
  'https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png';

const handleZoneImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = ZONE_IMAGE_PLACEHOLDER;
};

const renderProductCell = (cell, cellKey) => {
  if (cell.kind === 'overflow') {
    return (
      <div
        key={cellKey}
        className="zone-index__zone-product zone-index__zone-product--overflow"
      >
        <span>+{cell.count}</span>
      </div>
    );
  }

  const { product } = cell;
  const imageSrc =
    product?.product_image ||
    product?.main_image ||
    ZONE_IMAGE_PLACEHOLDER;
  const isSold = productHasSoldQty(product);

  return (
    <div
      key={cellKey}
      className={`zone-index__zone-product${
        isSold ? ' zone-index__zone-product--sold' : ''
      }`}
    >
      <img
        src={imageSrc}
        alt={product?.SKU || product?.name || ''}
        loading="lazy"
        onError={handleZoneImageError}
      />
      {isSold ? (
        <span
          className="zone-index__zone-product-overlay zone-index__zone-product-overlay--sold"
          aria-hidden
        />
      ) : null}
    </div>
  );
};

const DRAG_SCROLL_THRESHOLD_PX = 8;

const ZoneCard = ({ zone, t, disableIssueNavigation = false }) => {
  const navigate = useNavigate();
  const products = zone?.products || [];
  const productCount = products.length;
  const centerProducts = shouldCenterZoneProducts(productCount);
  const gridColumns = getZoneGridColumns(productCount);
  const rows = buildZoneProductGridRows(
    products,
    centerProducts ? 4 : gridColumns
  );
  const { current, max } = parseZoneTotal(zone?.total);
  const totalAlert = isZoneTotalCountAlert(zone);
  const hasSoldAlert = zoneHasSoldProducts(zone);
  const openIssueId = getZoneOpenIssueId(zone);
  const isClickable = Boolean(openIssueId) && !disableIssueNavigation;

  const openIssuePage = (event) => {
    if (!isClickable) return;
    event.preventDefault();
    event.stopPropagation();
    navigate(`/app/inventory/zone/edit/${openIssueId}`);
  };

  return (
    <article
      className={`zone-index__zone-card${
        hasSoldAlert ? ' zone-index__zone-card--alert' : ''
      }${isClickable ? ' zone-index__zone-card--clickable' : ''}${
        centerProducts ? ' zone-index__zone-card--centered-products' : ''
      }${!centerProducts && gridColumns < 8 ? ' zone-index__zone-card--sparse' : ''}`}
      style={
        centerProducts
          ? undefined
          : {
              '--zi-grid-columns': gridColumns,
              '--zi-grid-body-rows': Math.max(rows.length, 1),
            }
      }
      onClick={openIssuePage}
      onKeyDown={(event) => {
        if (!isClickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openIssuePage();
        }
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {hasSoldAlert ? (
        <span className="zone-index__zone-card-alert-dot" aria-hidden />
      ) : null}

      <div className="zone-index__zone-card-body">
        {rows.length ? (
          rows.map((row, rowIndex) => (
            <div
              key={`${zone?.zone_record_id || zone?.zone_id}-row-${rowIndex}`}
              className="zone-index__zone-card-row"
            >
              {row.map((cell, cellIndex) =>
                renderProductCell(
                  cell,
                  `${zone?.zone_record_id || zone?.zone_id}-cell-${rowIndex}-${cellIndex}`
                )
              )}
            </div>
          ))
        ) : (
          <div className="zone-index__zone-card-empty">{t('No products')}</div>
        )}
      </div>

      <footer className="zone-index__zone-card-footer">
        <div className="zone-index__zone-card-title">
          <span className="zone-index__zone-card-icon">
            {zone?.zone_logo ? (
              <img
                src={zone.zone_logo}
                alt=""
                aria-hidden
                onError={handleZoneImageError}
              />
            ) : (
              <span className="zone-index__zone-card-icon-fallback" aria-hidden />
            )}
          </span>
          <span className="zone-index__zone-card-name">{zone?.zone || '-'}</span>
        </div>

        <div className="zone-index__zone-card-total">
          <span className="zone-index__zone-card-total-label">{t('Total')}</span>
          <span className="zone-index__zone-card-total-values">
            <span
              className={`zone-index__zone-card-total-current${
                totalAlert ? ' zone-index__zone-card-total-current--alert' : ''
              }`}
            >
              {current}
            </span>
            <span className="zone-index__zone-card-total-separator">/</span>
            <span className="zone-index__zone-card-total-max">{max}</span>
          </span>
        </div>
      </footer>
    </article>
  );
};

const ZonesRow = ({ children }) => {
  const rowRef = useRef(null);
  const dragState = useRef({
    pointerDown: false,
    dragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const resetDragState = useCallback(() => {
    dragState.current = {
      pointerDown: false,
      dragging: false,
      startX: 0,
      scrollLeft: 0,
    };
  }, []);

  const endDrag = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;

    if (dragState.current.dragging) {
      const blockClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      el.addEventListener('click', blockClick, { capture: true, once: true });
    }

    el.classList.remove('zone-index__grid-zones-row--dragging');
    resetDragState();
  }, [resetDragState]);

  const handleMouseDown = useCallback((event) => {
    if (event.button !== 0) return;

    const el = rowRef.current;
    if (!el) return;

    dragState.current = {
      pointerDown: true,
      dragging: false,
      startX: event.pageX,
      scrollLeft: el.scrollLeft,
    };
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!dragState.current.pointerDown) return;

    const el = rowRef.current;
    if (!el) return;

    const delta = event.pageX - dragState.current.startX;

    if (!dragState.current.dragging) {
      if (Math.abs(delta) < DRAG_SCROLL_THRESHOLD_PX) return;
      dragState.current.dragging = true;
      el.classList.add('zone-index__grid-zones-row--dragging');
    }

    event.preventDefault();
    el.scrollLeft = dragState.current.scrollLeft - delta;
  }, []);

  useEffect(() => {
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [endDrag, handleMouseMove]);

  return (
    <div
      ref={rowRef}
      className="zone-index__grid-zones-row"
      onMouseDown={handleMouseDown}
    >
      {children}
    </div>
  );
};

export default function ZoneIndexGridView({
  locations = [],
  appliedFilters,
  searchQuery = '',
  t,
  disableIssueNavigation = false,
}) {
  const filteredLocations = filterLocationsForZoneIndex(locations, appliedFilters);
  const locationsWithZones = getLocationsWithZones(filteredLocations);
  const visibleLocations = filterLocationsByGridSearch(
    locationsWithZones,
    searchQuery
  );

  if (!visibleLocations.length) {
    return (
      <div className="zone-index__empty zone-index__empty--grid">
        {t('No Data Available In Table')}
      </div>
    );
  }

  return (
    <div className="zone-index__grid-view">
      {visibleLocations.map((location) => (
        <section
          key={location.location_id}
          className="zone-index__grid-location"
        >
          <header className="zone-index__grid-location-header">
            <span className="zone-index__grid-location-name">
              {location?.name || '-'}
            </span>
            <span className="zone-index__grid-location-count">
              ({location.zones.length} {t('Zones')})
            </span>
          </header>

          <ZonesRow>
            {location.zones.map((zone) => (
              <ZoneCard
                key={zone.zone_record_id || `${location.location_id}-${zone.zone_id}`}
                zone={zone}
                t={t}
                disableIssueNavigation={disableIssueNavigation}
              />
            ))}
          </ZonesRow>
        </section>
      ))}
    </div>
  );
}
